/**
 * @fileoverview Componente Atom para visualización de imágenes con soporte multi-plataforma y gestión de estados
 * @module ImageMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import LoadingMC from "./LoadingMC";
import useMediaUI from "../hooks/shared/ui/useMediaUI";
import useMediaData from "../hooks/shared/data/useMediaData";

/**
 * @typedef {Object} ImageData
 * @description Estructura de datos del archivo de imagen recibido desde la API
 * @property {string} id - Identificador único del archivo de imagen
 * @property {string} contentMediaUrl - URL principal del archivo de imagen
 */

/**
 * Componente Atom para visualización de imágenes con soporte multi-plataforma.
 * 
 * Maneja la visualización de archivos de imagen desde diferentes plataformas (WhatsApp, Messenger, Instagram)
 * con gestión automática de estados de carga, errores y optimización de renders.
 * 
 * @component ImageMC
 * @category Component - Atom
 * 
 * @description
 * **Características:**
 * - Visualización de imágenes con elemento HTML nativo
 * - Soporte multi-plataforma (WhatsApp, Messenger, Instagram)
 * - Gestión automática de estados de carga y errores
 * - Texto alternativo internacionalizado
 * - Eventos de click personalizables
 * - Optimización de renders con React.memo
 * 
 * **Atomic Design:**
 * - **Nivel**: Atom
 * - **Composición**: Elemento HTML img + componente LoadingMC
 * - **Dependencias**: LoadingMC (atom)
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   platform: string,             // Plataforma de origen ('whatsapp'|'messenger'|'instagram')
 *   data: ImageData,              // Datos del archivo de imagen
 *   onClick?: function,           // Handler para eventos de click
 *   alt?: string,                 // Texto alternativo personalizado
 *   className?: string,           // Clases CSS adicionales
 *   urlField?: string            // Campo URL personalizado (default: 'contentMediaUrl')
 * }
 * ```
 * 
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Atom Level**: Componente fundamental para visualización de imágenes
 * - ✅ **Hook Composition**: Separación estricta de Data Layer y UI Layer
 * - ✅ **Performance**: React.memo, optimización de carga y renders
 * - ✅ **Error Handling**: Gestión integral de errores de carga
 * - ✅ **Accessibility**: Texto alternativo automático e internacionalizado
 * - ✅ **Multi-platform**: Soporte para diferentes fuentes de imagen
 * 
 * **Estados de Componente:**
 * - **loading**: Muestra LoadingMC mientras carga la imagen
 * - **error**: Muestra mensaje de error cuando falla la carga
 * - **ready**: Muestra imagen cuando está lista y cargada
 * 
 * **Flujo de Operación:**
 * 1. **Inicialización**: useMediaData extrae URL del campo especificado
 * 2. **Configuración UI**: useMediaUI configura handlers específicos para imágenes
 * 3. **Carga**: Elemento img inicia carga automática
 * 4. **Error/Success**: Manejo automático de estados basado en eventos HTML
 * 5. **Interacción**: Click handler opcional para funcionalidad adicional
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.platform - Plataforma de origen de la imagen ('whatsapp'|'messenger'|'instagram')
 * @param {ImageData} props.data - Objeto con datos del archivo de imagen desde la API
 * @param {function} [props.onClick] - Función callback para manejar eventos de click en la imagen
 * @param {string} [props.alt] - Texto alternativo personalizado para la imagen
 * @param {string} [props.className] - Clases CSS adicionales para personalizar estilos
 * @param {string} [props.urlField='contentMediaUrl'] - Campo del objeto data que contiene la URL de la imagen
 * 
 * @returns {JSX.Element} Elemento JSX que renderiza la imagen o mensaje de error
 * 
 * @example
 * ```jsx
 * // Uso básico con imagen de WhatsApp
 * <ImageMC 
 *   platform="whatsapp"
 *   data={{
 *     id: "img123",
 *     contentMediaUrl: "https://api.example.com/image.jpg"
 *   }}
 *   alt="Imagen del cliente"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso con click handler y estilos personalizados
 * <ImageMC 
 *   platform="messenger"
 *   data={{
 *     id: "img456",
 *     contentMediaUrl: "https://graph.facebook.com/image.png"
 *   }}
 *   onClick={() => openLightbox()}
 *   className="tw-cursor-pointer tw-rounded-lg tw-shadow-md"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso con campo URL personalizado
 * <ImageMC 
 *   platform="instagram"
 *   data={{
 *     id: "img789",
 *     thumbnailUrl: "https://instagram.com/thumb.jpg",
 *     contentMediaUrl: "https://instagram.com/full.jpg"
 *   }}
 *   urlField="thumbnailUrl"
 *   className="tw-w-20 tw-h-20 tw-object-cover"
 * />
 * ```
 * 
 * @see {@link useMediaData} Hook de Data Layer para gestión de URLs de medios
 * @see {@link useMediaUI} Hook de UI Layer para lógica de interfaz de medios
 * @see {@link LoadingMC} Componente atom para estados de carga
 * 
 * @since 1.0.0
 * @version 3.1.0 - Implementación de separación de hooks por layers y optimización de performance
 */
const ImageMC = memo(({ platform, data, onClick, alt, className, urlField = 'contentMediaUrl' }) => {
  /**
   * Hook de internacionalización para acceder a las traducciones del módulo Inbox.
   * 
   * @memberof ImageMC
   * @private
   */
  const { t } = useTranslation('InboxMC');

  /**
   * Hook de Data Layer para gestión de URLs y estados de datos del archivo de imagen.
   * 
   * Maneja la extracción de URLs desde el objeto data y gestiona los estados
   * relacionados con la carga de datos (loading, error).
   * 
   * @memberof ImageMC
   * @private
   * 
   * @see {@link useMediaData} Para documentación completa del hook
   */
  // Data Layer
  const { url, setUrl, loading, error, setLoading, setError } = useMediaData({ data, urlField });

  /**
   * Hook de UI Layer para gestión de lógica de interfaz específica de medios de imagen.
   * 
   * Configura handlers de eventos, claves de React y fuentes de medios optimizadas
   * para visualización de imágenes en diferentes plataformas.
   * 
   * @memberof ImageMC
   * @private
   * 
   * @description
   * **Retornos del Hook:**
   * - `handleMediaError`: Handler optimizado para errores de carga de imagen
   * - `mediaKey`: Clave única para forzar re-render del elemento img
   * - `mediaSrc`: URL procesada y optimizada para visualización
   * 
   * @see {@link useMediaUI} Para documentación completa del hook
   */
  // UI Layer: lógica genérica de media
  const {
    handleMediaError,
    mediaKey,
    mediaSrc
  } = useMediaUI({
    platform,
    url,
    data,
    setUrl,
    setError,
    setLoading,
    mediaType: 'image',
  });

  if (!url) return null;
 
  return (
    <>
      <LoadingMC loading={loading} />
      {error ? (
        <blockquote className="tw-text-center tw-mt-20 tw-text-xs">
          {t('image.not_available')}
        </blockquote>
      ) : (
        <img
          key={mediaKey}
          src={mediaSrc}
          alt={alt || t('image.alt')}
          onClick={onClick}
          onError={handleMediaError}
          onLoad={() => setLoading(false)}
          className={`${className} ${loading ? 'tw-hidden' : ''}`}
        />
      )}
    </>
  );
});

ImageMC.displayName = "ImageMC";

export default ImageMC;